import { Component, Vue } from 'vue-property-decorator';
import moment from 'moment';
import { startCase } from 'lodash';
import { RewardPeriodDTO, UserDTO } from '@/models';
import { DATE_FORMAT, DEFAULT_DISPLAY_VALUE, PRIZE_TYPES } from '@/constants';
import { formatDateToLocalDateTime, formatDateToVTextField, cleanSettings } from '@/helpers';
import { updateRewardPeriod } from '@/services';
import Avatar from '@/components/common/avatar/Avatar.vue';
import ConfirmationDialog from '@/components/common/confirmation-dialog/ConfirmationDialog.vue';

interface TypeOption {
  text: string;
  value: string;
}

@Component({
  components: {
    Avatar,
    ConfirmationDialog
  }
})
export default class RewardPeriodDetailInformation extends Vue {
  $refs!: {
    formRef: HTMLFormElement;
  };

  private rewardPeriodFormData: RewardPeriodDTO = {} as RewardPeriodDTO;
  private isEditing = false;
  private dirtyCheckDialogShown = false;
  private typeOptions: TypeOption[] = [];
  private ruleOptions = {
    name: [(v: string) => !!v || 'Reward period name is required'],
    startDate: [
      (v: string) => !!v || 'Start date is required',
      (v: string) => (v && this.validateDateFields()) || 'Start date must be before end date'
    ],
    endDate: [
      (v: string) => !!v || 'End date is required',
      (v: string) => (v && this.validateDateFields()) || 'End date must be after start date'
    ]
  };
  private rewardPeriodId = -1;

  get rewardPeriod(): RewardPeriodDTO {
    return this.$store.getters['rewardPeriod/rewardPeriod'];
  }

  buildRewardPeriodFormData(): RewardPeriodDTO {
    const baseSettings: Record<string, any> = {};
    for (const opt of this.typeOptions) {
      baseSettings[opt.value] = '';
    }
    if (this.rewardPeriod.settings) {
      for (const key of Object.keys(this.rewardPeriod.settings)) {
        if (this.rewardPeriod.settings[key] > 0) {
          baseSettings[key] = this.rewardPeriod.settings[key];
        }
      }
    }
    return {
      ...this.rewardPeriod,
      startDate: formatDateToVTextField(this.rewardPeriod.startDate),
      endDate: formatDateToVTextField(this.rewardPeriod.endDate),
      settings: baseSettings
    };
  }

  get creator(): UserDTO {
    return this.rewardPeriod.createdBy;
  }

  validateDateFields(): boolean {
    const { startDate, endDate } = this.rewardPeriodFormData;
    return !startDate || !endDate || moment(startDate).isBefore(moment(endDate));
  }

  formValidate(): boolean {
    return this.$refs.formRef.validate();
  }

  switchToEditMode(): void {
    this.rewardPeriodFormData = this.buildRewardPeriodFormData();
    this.isEditing = true;
  }

  switchToViewMode(): void {
    if (this.isDirty()) {
      this.dirtyCheckDialogShown = true;
    } else {
      this.isEditing = false;
    }
  }

  isDirty(): boolean {
    const { rewardPeriod, rewardPeriodFormData } = this;
    const originalSettings = rewardPeriod.settings || {};
    const formSettings = rewardPeriodFormData.settings || {};
    const settingsChanged = Object.keys(formSettings).some(
      key => formSettings[key] !== originalSettings[key]
    );
    return (
      rewardPeriodFormData.name !== rewardPeriod.name ||
      rewardPeriodFormData.description !== rewardPeriod.description ||
      this.isDateDirty(rewardPeriodFormData.startDate, rewardPeriod.startDate) ||
      this.isDateDirty(rewardPeriodFormData.endDate, rewardPeriod.endDate) ||
      settingsChanged
    );
  }

  isDateDirty(date: string, originalDate: string): boolean {
    if (!date && !originalDate) {
      return false;
    }
    return formatDateToVTextField(date) !== formatDateToVTextField(originalDate);
  }

  onDirtyDialogConfirmed(): void {
    this.dirtyCheckDialogShown = false;
    this.isEditing = false;
  }

  async saveEdit(): Promise<void> {
    if (this.formValidate()) {
      const body = {
        id: this.rewardPeriodFormData.id,
        name: this.rewardPeriodFormData.name,
        description: this.rewardPeriodFormData.description,
        createdById: this.rewardPeriodFormData.createdBy.id,
        startDate: formatDateToLocalDateTime(this.rewardPeriodFormData.startDate),
        endDate: formatDateToLocalDateTime(this.rewardPeriodFormData.endDate),
        settings: cleanSettings(this.rewardPeriodFormData.settings)
      };
      const res = await updateRewardPeriod(body);
      this.$store.commit('rewardPeriod/setRewardPeriodInformation', res.data);
      this.isEditing = false;
    }
  }

  getDisplayDate(date: string): string {
    if (date) {
      return moment(date).format(DATE_FORMAT.DISPLAY);
    }
    return DEFAULT_DISPLAY_VALUE.DATE;
  }

  async created(): Promise<void> {
    this.rewardPeriodId = parseInt(this.$route.params.id);
    this.typeOptions = Object.keys(PRIZE_TYPES).map(pt => ({
      text: startCase(pt.toLowerCase()),
      value: pt
    }));
  }

  getDisplayRedemptionLimitsPerPrizeType(type: string): string {
    if (this.rewardPeriod.settings && this.rewardPeriod.settings[type]) {
      return this.rewardPeriod.settings[type].toString();
    }
    return 'Unlimited';
  }
}
